import { BasePagination, Box, HStack, Icon, SimpleGrid, Text, Tooltip, VStack } from '@elkaso-app/web-design';
import { IPocAgentCallData, useGetPocAgentCallsApi } from 'apis/poc-agent-calls/use-get-poc-agent-calls-api';
import { useGetPocAgentCallsStatsApi } from 'apis/poc-agent-calls/use-get-poc-agent-calls-stats-api';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { BaseSortButton } from 'components/base-table/sorting';
import { usePageParams } from 'hooks/use-page-params';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect } from 'react';
import { HiEmojiHappy, HiEmojiSad, HiPhone, HiPhoneMissedCall, HiUserGroup } from 'react-icons/hi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { IPocActionsCell, PocActionsCell } from '../components/poc-actions-cell';
import { IPocAgentDetailsCell, PocAgentDetailsCell } from '../components/poc-agent-details-cell';
import { IPocBrandDetailsCell, PocBrandDetailsCell } from '../components/poc-brand-details-cell';
import { IPocCallDetailsCell, PocCallDetailsCell } from '../components/poc-call-details-cell';
import { IPocClientPhoneNumberCell, PocClientPhoneNumberCell } from '../components/poc-client-phone-number-cell';

export const PocAgentCallsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const { getPageParams, setPageParams } = usePageParams();
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, data } = useGetPocAgentCallsApi();
  const { isLoading: isLoadingStats, isFetching: isFetchingStats, data: statsData } = useGetPocAgentCallsStatsApi();
  const { pageCount, total, data: pocData } = data ?? {};

  // Get current sentiment filters from URL
  const currentParams = getPageParams();
  const currentSentiments = currentParams.sentiment
    ? Array.isArray(currentParams.sentiment)
      ? currentParams.sentiment
      : [currentParams.sentiment]
    : [];

  const currentFilters = currentParams.filter
    ? Array.isArray(currentParams.filter)
      ? currentParams.filter
      : [currentParams.filter]
    : [];

  const handleSentimentFilter = (sentimentValue: string) => {
    const params = getPageParams();
    let sentiments = params.sentiment
      ? Array.isArray(params.sentiment)
        ? [...params.sentiment]
        : [params.sentiment]
      : [];

    if (sentiments.includes(sentimentValue)) {
      // Remove sentiment if already selected
      sentiments = sentiments.filter((s) => s !== sentimentValue);
    } else {
      // Add sentiment if not selected
      sentiments.push(sentimentValue);
    }

    // Update URL parameters
    if (sentiments.length === 0) {
      const restParams = { ...params };
      delete restParams.sentiment;
      setPageParams(restParams);
    } else {
      setPageParams({ ...params, sentiment: sentiments });
    }
  };

  const handleFilterToggle = (filterValue: string) => {
    const params = getPageParams();
    let filters = params.filter ? (Array.isArray(params.filter) ? [...params.filter] : [params.filter]) : [];

    if (filters.includes(filterValue)) {
      // Remove filter if already selected
      filters = filters.filter((f) => f !== filterValue);
    } else {
      // Add filter if not selected
      filters.push(filterValue);
    }

    // Update URL parameters
    if (filters.length === 0) {
      const restParams = { ...params };
      delete restParams.filter;
      setPageParams(restParams);
    } else {
      setPageParams({ ...params, filter: filters });
    }
  };

  useEffect(() => {
    dispatch(changeTitle('POC Agent Calls'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='Brand' sortButtonComponent={<BaseSortButton sortBy='brand.id' />} />,
        accessor: 'brand',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['brand']>) => (
          <CustomTableCell<IPocBrandDetailsCell> as={PocBrandDetailsCell} name={value.name} />
        ),
      },
      {
        Header: <TableHeader name='Agent' sortButtonComponent={<BaseSortButton sortBy='agentId' />} />,
        accessor: 'agent',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['agent']>) => (
          <CustomTableCell<IPocAgentDetailsCell>
            as={PocAgentDetailsCell}
            agentProvider={value.agentProvider}
            agentId={value.agentId}
            phoneNumber={value.phoneNumber}
          />
        ),
      },
      {
        Header: <TableHeader name='To' />,
        accessor: 'toNumber',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['toNumber']>) => (
          <CustomTableCell<IPocClientPhoneNumberCell> as={PocClientPhoneNumberCell} phoneNumber={value} />
        ),
      },
      {
        Header: <TableHeader name='Call Details' />,
        accessor: 'callDetails',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['callDetails']>) => (
          <CustomTableCell<IPocCallDetailsCell>
            as={PocCallDetailsCell}
            callId={value.callId}
            callStatus={value.callStatus}
            durationMs={value.durationMs}
            callCost={value.callCost}
            recordingsUrl={value.recordingsUrl}
            ingestedAt={value.ingestedAt}
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<IPocActionsCell> as={PocActionsCell} data={value} />
        ),
      },
    ],
    []
  );

  interface IColumnsAccessorData {
    id: number;
    brand: {
      id: number;
      name: string;
    };
    agent: {
      agentProvider: string;
      agentId: string;
      phoneNumber: string;
    };
    callDetails: {
      callId: string;
      callType: string;
      callStatus: string;
      durationMs: number;
      callCost: {
        combinedCost: number;
      };
      recordingsUrl: string;
      ingestedAt: string;
    };
    transcriptObject: {
      role: 'user' | 'agent';
      content: string;
    }[];
    disconnectionReason: string;
    callAnalysis: {
      summary: string;
      sentiment: string;
      isSuccessful: boolean;
      contactDetailsText: string;
    };
    toNumber: string;
    internalFeedback: string | null;
    actions: {
      id: number;
      internalFeedback: string | null;
    };
  }

  const columnsAccessorData = React.useMemo(
    () =>
      pocData?.map((item: IPocAgentCallData): IColumnsAccessorData => {
        const transcriptObject =
          item?.transcriptObject?.map((item) => {
            return {
              role: item?.role as 'user' | 'agent',
              content: item?.content || '',
            };
          }) || [];

        const callAnalysis = {
          summary: item?.callAnalysis?.call_summary || '',
          sentiment: item?.callAnalysis?.user_sentiment || '',
          isSuccessful: item?.callAnalysis?.call_successful || false,
          contactDetailsText: item?.callAnalysis?.custom_analysis_data?.contact_details || '',
        };

        return {
          id: item?.id,
          brand: {
            id: item?.brand?.id,
            name: item?.brand?.name,
          },
          agent: {
            agentProvider: item?.aiAgentProvider,
            agentId: item?.agentId,
            phoneNumber: item?.fromNumber,
          },
          callDetails: {
            callId: item?.callId,
            callType: item?.callType,
            callStatus: item?.callStatus,
            durationMs: item?.durationMs,
            callCost: {
              combinedCost: item?.callCost?.combined_cost,
            },
            recordingsUrl: item?.recordingUrl,
            ingestedAt: item?.ingestedAt,
          },
          transcriptObject,
          disconnectionReason: item?.disconnectionReason,
          callAnalysis,
          toNumber: item?.toNumber,
          internalFeedback: item?.internalFeedback,
          actions: {
            id: item?.id || 0,
            internalFeedback: item?.internalFeedback,
          },
        };
      }),
    [pocData]
  );

  const StatCard = ({
    title,
    value,
    icon,
    isLoading,
    onClick,
    isActive,
  }: {
    title: string;
    value: number | React.ReactNode;
    icon: React.ReactNode;
    isLoading?: boolean;
    onClick?: () => void;
    isActive?: boolean;
  }) => {
    return (
      <Box
        p={6}
        bg={isActive ? 'blue.50' : 'white'}
        borderRadius='lg'
        shadow='sm'
        border={isActive ? '2px solid' : '1px solid'}
        borderColor={isActive ? 'blue.500' : 'gray.200'}
        cursor={onClick ? 'pointer' : 'default'}
        onClick={onClick}
        _hover={onClick ? { shadow: 'md', transform: 'translateY(-1px)' } : {}}
        transition='all 0.2s'>
        <HStack spacing={4} align='center'>
          <Box p={2} bg={isActive ? 'blue.100' : 'gray.50'} borderRadius='md'>
            {icon}
          </Box>
          <VStack align='start' spacing={1}>
            <Text fontSize='sm' color='gray.500'>
              {title}
            </Text>
            {isLoading ? (
              <Box h='32px' w='80px' bg='gray.100' borderRadius='md' />
            ) : (
              <Text fontSize='2xl' fontWeight='bold' color={isActive ? 'blue.600' : 'inherit'}>
                {value}
              </Text>
            )}
          </VStack>
        </HStack>
      </Box>
    );
  };

  return (
    <>
      <Box mb={10}>
        <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={6}>
          <StatCard
            title='Total Calls'
            value={
              <VStack spacing={0} align='start'>
                <Text fontSize='2xl' fontWeight='bold'>
                  {statsData?.totalCalls || 0}
                </Text>
                <Text fontSize='sm' color='blue.600' fontWeight='medium'>
                  {statsData?.totalCost?.currencySymbol || '$'}
                  {Number(statsData?.totalCost?.value || 0).toFixed(2)} Total Cost
                </Text>
                <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                  {(() => {
                    const totalCalls = statsData?.totalCalls || 0;
                    const totalCost = Number(statsData?.totalCost?.value || 0);
                    const avgPrice = totalCalls > 0 ? totalCost / totalCalls : 0;
                    return `${statsData?.totalCost?.currencySymbol || '$'}${avgPrice.toFixed(2)} Avg per Call`;
                  })()}
                </Text>
              </VStack>
            }
            icon={<Icon as={HiPhone} boxSize='24px' color='blue.500' />}
            isLoading={isLoadingStats || isFetchingStats}
          />
          <StatCard
            title='Total Contacts'
            value={
              <VStack spacing={0} align='start'>
                <Text fontSize='2xl' fontWeight='bold'>
                  {statsData?.totalContacts || 0}
                </Text>
                <Tooltip
                  label='Success rate is calculated by dividing the number of contacts by the total number of answered calls (excluding missed calls)'
                  placement='bottom'
                  hasArrow>
                  <Text fontSize='sm' color='green.600' fontWeight='medium'>
                    {(() => {
                      const total = (statsData?.totalCalls || 0) - (statsData?.sentiments?.unknown || 0);
                      const successRate = total > 0 ? ((statsData?.totalContacts || 0) / total) * 100 : 0;
                      return `${successRate.toFixed(1)}% Success Rate`;
                    })()}
                  </Text>
                </Tooltip>
              </VStack>
            }
            icon={<Icon as={HiUserGroup} boxSize='24px' color='green.500' />}
            isLoading={isLoadingStats || isFetchingStats}
            onClick={() => handleFilterToggle('only_contacts')}
            isActive={currentFilters.includes('only_contacts')}
          />
          <StatCard
            title='Positive Sentiment'
            value={statsData?.sentiments?.positive || 0}
            icon={<Icon as={HiEmojiHappy} boxSize='24px' color='yellow.500' />}
            isLoading={isLoadingStats || isFetchingStats}
            onClick={() => handleSentimentFilter('positive')}
            isActive={currentSentiments.includes('positive')}
          />
          <StatCard
            title='Neutral Sentiment'
            value={statsData?.sentiments?.neutral || 0}
            icon={<Icon as={HiEmojiSad} boxSize='24px' color='gray.500' />}
            isLoading={isLoadingStats || isFetchingStats}
            onClick={() => handleSentimentFilter('neutral')}
            isActive={currentSentiments.includes('neutral')}
          />
          <StatCard
            title='Negative Sentiment'
            value={statsData?.sentiments?.negative || 0}
            icon={<Icon as={HiEmojiSad} boxSize='24px' color='red.500' />}
            isLoading={isLoadingStats || isFetchingStats}
            onClick={() => handleSentimentFilter('negative')}
            isActive={currentSentiments.includes('negative')}
          />
          <StatCard
            title='No Answer / Dial Failed'
            value={statsData?.sentiments?.unknown || 0}
            icon={<Icon as={HiPhoneMissedCall} boxSize='24px' color='gray.500' />}
            isLoading={isLoadingStats || isFetchingStats}
            onClick={() => handleSentimentFilter('unknown')}
            isActive={currentSentiments.includes('unknown')}
          />
        </SimpleGrid>
      </Box>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='POC Agent Calls' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <ClearFilterButton />
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={columnsAccessorData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>
    </>
  );
};
